import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formatCurrency, formatDateTime, getTimeUntilExpiration } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import { ChevronDownIcon, ChevronUpIcon, ClockIcon } from '@heroicons/react/24/outline';

// Component for truncated description with expand/collapse
const TruncatedDescription = ({ description, maxWords = 2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const words = description.split(' ');
  const shouldTruncate = words.length > maxWords;
  const displayText = isExpanded || !shouldTruncate ? description : words.slice(0, maxWords).join(' ') + '...';
  
  return (
    <div className="text-gray-700 text-sm">
      {displayText}
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon className="w-3 h-3 inline" />
              <span className="ml-1">Less</span>
            </>
          ) : (
            <>
              <ChevronDownIcon className="w-3 h-3 inline" />
              <span className="ml-1">More</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

const PaymentCard = ({ payment, onRefresh }) => {
  const location = useLocation();
  
  const getEffectiveStatus = (payment) => {
    if (payment.status === 'pending') {
      const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);
      return timeUntilExpiration.expired ? 'expired' : 'pending';
    }
    return payment.status;
  };

  const effectiveStatus = getEffectiveStatus(payment);
  const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);

  return (
    <div className="card card-hover">
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Client Info */}
            <div className="mb-3">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {payment.clientName}
              </h3>
              <p className="text-sm text-gray-500 truncate">
                {payment.clientEmail}
              </p>
            </div>

            {/* Description */}
            <div className="mb-3">
              <TruncatedDescription description={payment.description} maxWords={2} />
            </div>

            {/* Amount and Status */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(payment.amount, payment.currency)}
              </div>
              <StatusBadge status={effectiveStatus} showIcon={true} />
            </div>

            {/* Time Info */}
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 mb-4">
              <div>
                <span className="font-medium">Created:</span>
                <br />
                {formatDateTime(payment.createdAt)}
              </div>
              <div>
                <span className="font-medium">Expires:</span>
                <br />
                <span className={timeUntilExpiration.expired ? 'text-red-600' : ''}>
                  {formatDateTime(payment.expiresAt)}
                </span>
              </div>
            </div>

            {/* Time Remaining for Pending Payments */}
            {effectiveStatus === 'pending' && !timeUntilExpiration.expired && (
              <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-md">
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 text-yellow-600 mr-2" />
                  <span className="text-sm text-warning-800">
                    Expires in {timeUntilExpiration.hours}h {timeUntilExpiration.minutes}m
                  </span>
                </div>
              </div>
            )}

            {/* Payment Link for Pending Payments */}
            {payment.paymentLink && effectiveStatus === 'pending' && !timeUntilExpiration.expired && (
              <div className="mb-4">
                <a
                  href={payment.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success w-full"
                >
                  View Payment Link
                </a>
              </div>
            )}

            {/* Stripe Link for Paid Payments */}
            {payment.stripePaymentIntentId && effectiveStatus === 'paid' && (
              <div className="mb-4">
                <a
                  href={`https://dashboard.stripe.com/payments/${payment.stripePaymentIntentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary w-full"
                >
                  View in Stripe
                </a>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-2">
              <Link
                to={`/payment/${payment._id}`}
                state={{ from: location.pathname }}
                className="btn btn-primary flex-1"
              >
                View Details
              </Link>
              
              {effectiveStatus === 'failed' && (
                <button
                  onClick={() => {
                    // Copy payment link to clipboard
                    if (payment.paymentLink) {
                      navigator.clipboard.writeText(payment.paymentLink);
                      // You could add a toast notification here
                    }
                  }}
                  className="btn btn-secondary"
                >
                  Copy Link
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;
